import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SEOHead } from "@/components/SEOHead";
import { 
  Store, 
  ArrowLeft, 
  CheckCircle,
  Zap,
  TrendingUp,
  Users,
  CreditCard,
  Shield,
  Globe,
  User,
  Building2,
  UserCheck,
  Plus,
  X
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const SellerRegister = () => {
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [sellerType, setSellerType] = useState<"individual" | "small_group" | "business">("individual");
  const [partnerFirstNames, setPartnerFirstNames] = useState<string[]>([""]);
  const [partnerLastNames, setPartnerLastNames] = useState<string[]>([""]);

  // Business-specific state
  const [businessType, setBusinessType] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");
  const [businessIsland, setBusinessIsland] = useState<string>("");
  const navigate = useNavigate();
  const { signUp } = useAuth();

  // Partner names management functions
  const addPartnerField = () => {
    if (partnerFirstNames.length < 5) {
      setPartnerFirstNames([...partnerFirstNames, ""]);
      setPartnerLastNames([...partnerLastNames, ""]);
    }
  };

  const removePartnerField = (index: number) => {
    if (partnerFirstNames.length > 1) {
      setPartnerFirstNames(partnerFirstNames.filter((_, i) => i !== index));
      setPartnerLastNames(partnerLastNames.filter((_, i) => i !== index));
    }
  };

  const updatePartnerFirstName = (index: number, value: string) => {
    const updated = [...partnerFirstNames];
    updated[index] = value.trim();
    setPartnerFirstNames(updated);
  };

  const updatePartnerLastName = (index: number, value: string) => {
    const updated = [...partnerLastNames];
    updated[index] = value.trim();
    setPartnerLastNames(updated);
  };

  // Reset partner names when seller type changes
  const handleSellerTypeChange = (value: string) => {
    if (value === "individual" || value === "small_group" || value === "business") {
      setSellerType(value);
      // Reset partner names when switching away from small_group
      if (value !== "small_group") {
        setPartnerFirstNames([""]);
        setPartnerLastNames([""]);
      }
      // Reset business-specific fields when switching away from business
      if (value !== "business") {
        setBusinessType("");
        setIndustry("");
        setBusinessIsland("");
      }
    }
  };
  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Seller Registration Unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-center">
              Supabase authentication is not configured. Please check your environment variables.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData(e.currentTarget);
      const email = String(form.get("email") || "").trim();
      const password = String(form.get("password") || "");
      const storeName = String(form.get("storeName") || "").trim();
      const username = String(form.get("username") || "").trim().toLowerCase();
      const firstName = String(form.get("firstName") || "").trim();
      const lastName = String(form.get("lastName") || "").trim();
      const age = parseInt(String(form.get("age") || "0"));
      const phone = String(form.get("phone") || "").trim();
      const selectedSellerType = sellerType;

      // Business-specific fields validation
      let businessData = {};
      if (selectedSellerType === "business") {
        const companyName = String(form.get("companyName") || "").trim();
        const businessRegNumber = String(form.get("businessRegNumber") || "").trim();
        const businessLicenseNumber = String(form.get("businessLicenseNumber") || "").trim();
        const businessSettlement = String(form.get("businessSettlement") || "").trim();
        const businessCityTown = String(form.get("businessCityTown") || "").trim();
        const businessPhone = String(form.get("businessPhone") || "").trim();
        const businessEmail = String(form.get("businessEmail") || "").trim();
        const ownerContactName = String(form.get("ownerContactName") || "").trim();
        const dateOfIncorporation = String(form.get("dateOfIncorporation") || "");
        const businessDescription = String(form.get("businessDescription") || "").trim();

        // Validate required business fields
        if (!companyName || !businessType || !industry || !businessRegNumber || !businessIsland || !ownerContactName || !businessDescription) {
          toast.error("Please fill in all required business fields");
          setLoading(false);
          return;
        }

        if (businessEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessEmail)) {
          toast.error("Please enter a valid business email address");
          setLoading(false);
          return;
        }

        businessData = {
          company_name: companyName,
          business_type: businessType,
          industry_sector: industry,
          business_registration_number: businessRegNumber,
          business_license_number: businessLicenseNumber || null,
          business_island: businessIsland,
          business_settlement: businessSettlement || null,
          business_city_town: businessCityTown || null,
          business_phone: businessPhone || null,
          business_email: businessEmail || null,
          business_owner_contact_name: ownerContactName,
          date_of_incorporation: dateOfIncorporation || null,
          business_description: businessDescription
        };
      }
      // Validate partner names for small groups
      let validatedPartnerFirstNames: string[] = [];
      let validatedPartnerLastNames: string[] = [];
      if (selectedSellerType === "small_group") {
        validatedPartnerFirstNames = partnerFirstNames
          .map(name => name.trim())
          .filter(name => name.length > 0);

        validatedPartnerLastNames = partnerLastNames
          .map(name => name.trim())
          .filter(name => name.length > 0);

        // Check if we have at least one complete partner (first + last name)
        const completePartners = partnerFirstNames
          .map((firstName, index) => ({
            firstName: firstName.trim(),
            lastName: partnerLastNames[index]?.trim() || ""
          }))
          .filter(partner => partner.firstName && partner.lastName);

        if (completePartners.length === 0) {
          toast.error("Please add at least one complete partner name (first and last) for small group registration");
          setLoading(false);
          return;
        }

        if (completePartners.length > 5) {
          toast.error("Maximum 5 partners allowed for small group registration");
          setLoading(false);
          return;
        }

        // Ensure arrays are the same length for data integrity
        const maxPartners = Math.min(completePartners.length, 5);
        validatedPartnerFirstNames = completePartners.slice(0, maxPartners).map(p => p.firstName);
        validatedPartnerLastNames = completePartners.slice(0, maxPartners).map(p => p.lastName);

        // Additional validation: ensure no empty strings
        if (validatedPartnerFirstNames.some(name => !name.trim()) || 
            validatedPartnerLastNames.some(name => !name.trim())) {
          toast.error("All partner names must be complete (both first and last name required)");
          setLoading(false);
          return;
        }
      }
      // Basic validation
      if (!email || !password || !storeName || !username || !firstName || !lastName || !age || !selectedSellerType) {
        toast.error("Please fill in all required fields");
        setLoading(false);
        return;
      }

      if (age < 18 || age > 120) {
        toast.error("You must be at least 18 years old to become a seller");
        setLoading(false);
        return;
      }

      if (username.length < 3) {
        toast.error("Username must be at least 3 characters long");
        setLoading(false);
        return;
      }

      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        toast.error("Username can only contain letters, numbers, hyphens, and underscores");
        setLoading(false);
        return;
      }

      if (!agreedToTerms) {
        toast.error("Please agree to the Terms of Service and Privacy Policy");
        setLoading(false);
        return;
      }

      // Check if username is already taken
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      if (existingProfile) {
        toast.error("Username is already taken. Please choose a different one.");
        setLoading(false);
        return;
      }
      console.log("Attempting seller registration for:", email);

      const userMetadata = {
        role: 'seller',
        username,
        first_name: firstName,
        last_name: lastName,
        age,
        phone: phone || null,
        store_name: storeName,
        seller_type: sellerType,
        partner_first_names: selectedSellerType === 'small_group' ? validatedPartnerFirstNames : null,
        partner_last_names: selectedSellerType === 'small_group' ? validatedPartnerLastNames : null,
        onboarding_step: 'account_created',
        business_type: selectedSellerType === 'business' ? businessType : null,
        industry: selectedSellerType === 'business' ? industry : null,
        business_island: selectedSellerType === 'business' ? businessIsland : null,
        ...businessData
      };

      const { data, error } = await signUp(email, password, userMetadata);

      if (error) {
        console.error("Seller registration error:", error);

        // Handle specific email confirmation errors
        if (error.message.includes("email") && error.message.includes("confirmation")) {
          // Account was created but email confirmation failed
          toast.warning("Account created successfully, but we couldn't send the confirmation email. You can still proceed with setting up your store.");
          // Continue with profile creation since the account was created
        } else if (error.message.includes("sending confirmation email") || error.message.includes("Error sending confirmation email")) {
          // Account was created but email confirmation failed  
          toast.warning("Account created successfully! Email confirmation is temporarily unavailable, but you can proceed with your seller setup.");
          // Continue with profile creation since the account was created
        } else if (error.message.includes("User already registered")) {
          toast.error("An account with this email already exists. Please try signing in instead.");
          setLoading(false);
          return;
        } else if (error.message.includes("Invalid email")) {
          toast.error("Please enter a valid email address.");
          setLoading(false);
          return;
        } else if (error.message.includes("Password should be at least")) {
          toast.error("Password must be at least 6 characters long.");
          setLoading(false);
          return;
        } else {
          toast.error(error.message || "Registration failed. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Create user profile with the additional information
      if (data.user) {
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: data.user.id,
              username,
              first_name: firstName,
              last_name: lastName,
              age,
              phone: phone || null,
              full_name: `${firstName} ${lastName}`,
              seller_type: sellerType,
              partner_first_names: selectedSellerType === 'small_group' ? validatedPartnerFirstNames : null,
              partner_last_names: selectedSellerType === 'small_group' ? validatedPartnerLastNames : null,
              ...(selectedSellerType === 'business' ? businessData : {})
            });

          if (profileError) {
            console.error("Profile creation error:", profileError);
            // Don't block registration flow if profile creation fails
            // The user can complete their profile later
            if (profileError.code === "23505" && profileError.message.includes("username")) {
              toast.warning("Account created! However, the username is already taken. Please update your username in your profile settings.");
            } else {
              toast.warning("Account created! Please complete your profile setup after signing in to ensure full functionality.");
            }
          } else {
            console.log("✅ Profile created successfully");
          }
        } catch (profileErr) {
          console.error("Profile creation failed:", profileErr);
        }
      }
      console.log("Seller registration successful:", data);

      // Show appropriate success message based on email confirmation status
      if (data.user && !data.user.email_confirmed_at) {
        toast.success(`Welcome to Bahama Marketplace, ${firstName}! Your account has been created. Check your email to verify your account, or continue with setup.`);
      } else {
        toast.success(`Welcome to Bahama Marketplace, ${firstName}! Check your email to verify your account.`);
      }
      // Redirect to seller onboarding
      navigate("/seller/onboarding");

    } catch (err: any) {
      console.error("Unexpected seller registration error:", err);
      toast.error("An unexpected error occurred during registration");
    } finally {
      setLoading(false);
    }
  };

  const isAnyLoading = loading;

  const benefits = [
    {
      icon: TrendingUp,
      title: "Grow Your Business",
      description: "Reach thousands of customers across the Bahamas with tools tailored to your seller type"
    },
    {
      icon: Users,
      title: "Easy Management",
      description: "Intuitive dashboard with features designed for individuals, groups, and businesses"
    },
    {
      icon: CreditCard,
      title: "Fast Payments",
      description: "Get paid quickly with secure payment processing for all seller types"
    },
    {
      icon: Shield,
      title: "Trusted Platform",
      description: "Built-in fraud protection and dedicated support based on your business needs"
    }
  ];

  return (
    <>
      <SEOHead
        title="Become a Seller - Bahama Marketplace"
        description="Start selling on Bahama Marketplace today. Create your store, list products, and reach customers across the Bahamas."
      />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
        {/* Header */}
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">Back to Bahama Marketplace</span>
            </Link>
            <Link to="/login" className="text-sm text-primary hover:underline">
              Already a seller? Sign in
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-4 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-7xl mx-auto">
            {/* Left Side - Benefits */}
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Store className="h-4 w-4" />
                  Seller Program
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                  Start selling on <span className="text-primary">Bahama Marketplace</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Join thousands of sellers who trust Bahama Marketplace to grow their business. 
                  Set up your store in minutes and start reaching customers today.
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Free to start</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>No monthly fees</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Easy setup</span>
                  </div>
                </div>
              </div>

              <div className="grid gap-6">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex gap-4 p-4 rounded-lg bg-card border border-border/40">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{benefit.title}</h3>
                        <p className="text-sm text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                  <h3 className="font-semibold">Launch Special Offer</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">
                  For the first 100 sellers: Zero commission on your first 50 sales!
                </p>
                <div className="text-2xl font-bold text-primary">0% fees</div>
                <p className="text-xs text-muted-foreground">Limited time offer</p>
              </div>

              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="h-6 w-6 text-primary" />
                  <h3 className="font-semibold">Seller Type Benefits</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium">Individual:</span>
                      <span className="text-muted-foreground ml-1">Personal dashboard, easy listing tools</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <UserCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium">Small Group:</span>
                      <span className="text-muted-foreground ml-1">Team collaboration, shared analytics</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium">Business:</span>
                      <span className="text-muted-foreground ml-1">Advanced tools, priority support, bulk operations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Registration Form */}
            <div className="max-w-md mx-auto w-full">
              <Card className="shadow-xl border-border/40">
                <CardHeader>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Store className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Create Your Seller Account</CardTitle>
                    <CardDescription>
                      Start your journey as a Bahama Marketplace seller
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enhanced Registration Form */}
                  <form onSubmit={onSubmit} className="space-y-4">
                    {/* Store Name */}
                    <div>
                      <Label htmlFor="storeName">Store Name *</Label>
                      <Input
                        id="storeName"
                        name="storeName"
                        placeholder="John's Amazing Store"
                        required
                        disabled={loading}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        This will be your shop's display name
                      </p>
                    </div>
                    {/* Seller Type Selection */}
                    <div className="space-y-3">
                      <Label>Seller Type *</Label>
                      <RadioGroup 
                        value={sellerType} 
                        onValueChange={handleSellerTypeChange}
                        className="space-y-3"
                        disabled={loading}
                      >
                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                          <RadioGroupItem value="individual" id="individual" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <User className="h-4 w-4 text-primary" />
                              <label htmlFor="individual" className="font-medium cursor-pointer">
                                Individual Seller
                              </label>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Selling as a single person. Perfect for personal items, crafts, or side hustles.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                          <RadioGroupItem value="small_group" id="small_group" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <UserCheck className="h-4 w-4 text-primary" />
                              <label htmlFor="small_group" className="font-medium cursor-pointer">
                                Small Group / Partnership
                              </label>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              2-5 people working together. Great for family businesses or small partnerships.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                          <RadioGroupItem value="business" id="business" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Building2 className="h-4 w-4 text-primary" />
                              <label htmlFor="business" className="font-medium cursor-pointer">
                                Registered Business
                              </label>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Licensed business entity. Ideal for established companies and corporations.
                            </p>
                          </div>
                        </div>
                      </RadioGroup>
                      <p className="text-xs text-muted-foreground">
                        This helps us provide appropriate tools and support for your business type.
                      </p>
                    </div>

                    {/* Partner Names Section - Only shown for Small Group */}
                    {sellerType === "small_group" && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Partner Names *</Label>
                          <span className="text-xs text-muted-foreground">
                            {partnerFirstNames.length}/5 partners
                          </span>
                        </div>

                        <div className="space-y-3">
                          {partnerFirstNames.map((firstName, index) => (
                            <div key={index} className="space-y-2">
                              <div className="text-xs font-medium text-muted-foreground">
                                Partner {index + 1}
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  placeholder="First name"
                                  value={firstName}
                                  onChange={(e) => updatePartnerFirstName(index, e.target.value)}
                                  disabled={loading}
                                  className="flex-1"
                                  maxLength={50}
                                />
                                <Input
                                  placeholder="Last name"
                                  value={partnerLastNames[index] || ""}
                                  onChange={(e) => updatePartnerLastName(index, e.target.value)}
                                  disabled={loading}
                                  className="flex-1"
                                  maxLength={50}
                                />
                                {partnerFirstNames.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => removePartnerField(index)}
                                    disabled={loading}
                                    className="h-10 w-10 flex-shrink-0"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {partnerFirstNames.length < 5 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addPartnerField}
                            disabled={loading}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Partner ({partnerFirstNames.length}/5)
                          </Button>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Add the first and last names of your business partners (2-5 people). These will be stored securely and used for business verification if needed.
                        </p>
                      </div>
                    )}

                    {/* Business Information Section - Only shown for Business */}
                    {sellerType === "business" && (
                      <div className="space-y-4 border-t pt-4">
                        <div className="flex items-center gap-2 mb-4">
                          <Building2 className="h-5 w-5 text-primary" />
                          <h3 className="font-medium text-lg">Business Information</h3>
                        </div>

                        {/* Company Name */}
                        <div>
                          <Label htmlFor="companyName">Company Name *</Label>
                          <Input
                            id="companyName"
                            name="companyName"
                            placeholder="ABC Company Ltd."
                            required
                            disabled={loading}
                            className="mt-1"
                            maxLength={100}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Official registered company name
                          </p>
                        </div>

                        {/* Business Type & Industry */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Business Type *</Label>
                            <Select value={businessType} onValueChange={setBusinessType} disabled={loading}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select business type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sole_trader">Sole Trader</SelectItem>
                                <SelectItem value="partnership">Partnership</SelectItem>
                                <SelectItem value="limited_company">Limited Company</SelectItem>
                                <SelectItem value="corporation">Corporation</SelectItem>
                                <SelectItem value="llc">LLC</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Industry / Sector *</Label>
                            <Select value={industry} onValueChange={setIndustry} disabled={loading}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="retail">Retail & Consumer Goods</SelectItem>
                                <SelectItem value="food_beverage">Food & Beverage</SelectItem>
                                <SelectItem value="tourism_hospitality">Tourism & Hospitality</SelectItem>
                                <SelectItem value="construction">Construction & Real Estate</SelectItem>
                                <SelectItem value="healthcare">Healthcare & Medical</SelectItem>
                                <SelectItem value="automotive">Automotive</SelectItem>
                                <SelectItem value="technology">Technology & IT</SelectItem>
                                <SelectItem value="financial_services">Financial Services</SelectItem>
                                <SelectItem value="education">Education & Training</SelectItem>
                                <SelectItem value="arts_crafts">Arts & Crafts</SelectItem>
                                <SelectItem value="agriculture">Agriculture & Farming</SelectItem>
                                <SelectItem value="marine_fishing">Marine & Fishing</SelectItem>
                                <SelectItem value="professional_services">Professional Services</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Registration & License Numbers */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="businessRegNumber">Business Registration Number *</Label>
                            <Input
                              id="businessRegNumber"
                              name="businessRegNumber"
                              placeholder="123456789"
                              required
                              disabled={loading}
                              className="mt-1"
                              maxLength={50}
                            />
                          </div>

                          <div>
                            <Label htmlFor="businessLicenseNumber">Business License Number</Label>
                            <Input
                              id="businessLicenseNumber"
                              name="businessLicenseNumber"
                              placeholder="LIC-123456 (if applicable)"
                              disabled={loading}
                              className="mt-1"
                              maxLength={50}
                            />
                          </div>
                        </div>

                        {/* Business Address */}
                        <div className="space-y-3">
                          <Label>Business Address *</Label>
                          <div>
                            <Select value={businessIsland} onValueChange={setBusinessIsland} disabled={loading}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select island" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new_providence">New Providence</SelectItem>
                                <SelectItem value="grand_bahama">Grand Bahama</SelectItem>
                                <SelectItem value="abaco">Abaco</SelectItem>
                                <SelectItem value="eleuthera">Eleuthera</SelectItem>
                                <SelectItem value="cat_island">Cat Island</SelectItem>
                                <SelectItem value="san_salvador">San Salvador</SelectItem>
                                <SelectItem value="long_island">Long Island</SelectItem>
                                <SelectItem value="exuma">Exuma</SelectItem>
                                <SelectItem value="andros">Andros</SelectItem>
                                <SelectItem value="bimini">Bimini</SelectItem>
                                <SelectItem value="berry_islands">Berry Islands</SelectItem>
                                <SelectItem value="crooked_island">Crooked Island</SelectItem>
                                <SelectItem value="acklins">Acklins</SelectItem>
                                <SelectItem value="mayaguana">Mayaguana</SelectItem>
                                <SelectItem value="inagua">Inagua</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              name="businessSettlement"
                              placeholder="Settlement (optional)"
                              disabled={loading}
                              maxLength={100}
                            />
                            <Input
                              name="businessCityTown"
                              placeholder="City/Town (optional)"
                              disabled={loading}
                              maxLength={100}
                            />
                          </div>
                        </div>

                        {/* Business Contact Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="businessPhone">Business Phone</Label>
                            <Input
                              id="businessPhone"
                              name="businessPhone"
                              type="tel"
                              placeholder="+1 (242) 555-0123"
                              disabled={loading}
                              className="mt-1"
                              maxLength={20}
                            />
                          </div>

                          <div>
                            <Label htmlFor="businessEmail">Business Email</Label>
                            <Input
                              id="businessEmail"
                              name="businessEmail"
                              type="email"
                              placeholder="info@company.com"
                              disabled={loading}
                              className="mt-1"
                              maxLength={100}
                            />
                          </div>
                        </div>

                        {/* Owner/Contact Person & Incorporation Date */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="ownerContactName">Owner / Contact Person Name *</Label>
                            <Input
                              id="ownerContactName"
                              name="ownerContactName"
                              placeholder="John Doe"
                              required
                              disabled={loading}
                              className="mt-1"
                              maxLength={100}
                            />
                          </div>

                          <div>
                            <Label htmlFor="dateOfIncorporation">Date of Incorporation / Registration</Label>
                            <Input
                              id="dateOfIncorporation"
                              name="dateOfIncorporation"
                              type="date"
                              disabled={loading}
                              className="mt-1"
                            />
                          </div>
                        </div>

                        {/* Business Description */}
                        <div>
                          <Label htmlFor="businessDescription">Description of Business / Services *</Label>
                          <Textarea
                            id="businessDescription"
                            name="businessDescription"
                            placeholder="Describe your business activities, products, and services..."
                            required
                            disabled={loading}
                            className="mt-1 min-h-[100px]"
                            maxLength={500}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Briefly describe what your business does and what products/services you offer
                          </p>
                        </div>

                        <Separator />
                      </div>
                    )}
                    {/* Personal Information */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input 
                          required 
                          id="firstName" 
                          name="firstName" 
                          type="text" 
                          placeholder="John" 
                          className="mt-1" 
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input 
                          required 
                          id="lastName" 
                          name="lastName" 
                          type="text" 
                          placeholder="Doe" 
                          className="mt-1" 
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <Label htmlFor="username">Username *</Label>
                      <Input 
                        required 
                        id="username" 
                        name="username" 
                        type="text" 
                        placeholder="johndoe_store" 
                        className="mt-1" 
                        disabled={loading}
                        minLength={3}
                        maxLength={30}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        3-30 characters, letters, numbers, hyphens and underscores only
                      </p>
                    </div>

                    {/* Age */}
                    <div>
                      <Label htmlFor="age">Age *</Label>
                      <Input 
                        required 
                        id="age" 
                        name="age" 
                        type="number" 
                        placeholder="25" 
                        className="mt-1" 
                        disabled={loading}
                        min={18}
                        max={120}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Must be at least 18 years old to sell
                      </p>
                    </div>

                    {/* Contact Information */}
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        required
                        disabled={loading}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number (Optional)</Label>
                      <Input 
                        id="phone" 
                        name="phone" 
                        type="tel" 
                        placeholder="+1 (555) 123-4567" 
                        className="mt-1" 
                        disabled={loading}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Recommended for customer support and order updates
                      </p>
                    </div>

                    {/* Password */}
                    <div>
                      <Label htmlFor="password">Password *</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="••••••••"
                        required
                        minLength={6}
                        disabled={loading}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Must be at least 6 characters
                      </p>
                    </div>

                    <div className="flex items-start space-x-2 pt-2">
                      <Checkbox 
                        id="terms"
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                        disabled={loading}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="terms"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          I agree to the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Required to create your seller account
                        </p>
                      </div>
                    </div>

                    <Button 
                      type="submit"
                      disabled={loading || !agreedToTerms || !sellerType || (sellerType === "business" && (!businessType || !industry || !businessIsland))}
                      className="w-full"
                      size="lg"
                    >
                      {loading ? "Creating Your Store..." : "Create Seller Account"}
                    </Button>
                  </form>

                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Already have a seller account?
                    </p>
                    <Link to="/seller/login" className="text-sm text-primary hover:underline font-medium">
                      Sign in to your seller dashboard
                    </Link>
                  </div>

                  <Alert>
                    <Globe className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      By creating an account, you'll join Bahama Marketplace's growing community of local sellers. 
                      We'll help you set up your store and start selling in minutes.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerRegister;